/**
 * Where the client stands at every buyer-journey stage (2026-09-08).
 *
 * WHY THIS REPLACES TWO CHARTS. The bump chart drew the leaders' race and the
 * client was not on it — Lisa: "this report isn't for Abcam ... they're not
 * even in the top five." The dumbbell that replaced it compared the client to
 * each stage's most-recommended company, and she could not read it: "it made a
 * new data set and I don't understand it even more." What she DID read at a
 * glance was the per-stage leader grid — "this actually answers the question
 * that's missing above: where you sit against the others" — and Ramos asked
 * for exactly that information "up in the race chart, [so] now we've got a
 * visual and an explainer."
 *
 * THE FORM: small multiples — one ranked ladder per stage, top five by
 * visibility, and the client's own row drawn EVERY time: in place when they
 * make the ladder, beneath a divider with their rank when they do not. Bars
 * share one scale (share of that stage's answers) across all five panels, so
 * the shape of the journey reads left to right: who owns the late stages, and
 * how far below the line the client sits at each one.
 *
 * THE RECOMMENDATION IS DERIVED, NOT ASSERTED. The panel outlined in the
 * accent is the shortest path: the stage where the fewest additional answers
 * would put the client level with the fifth-placed company. The verdict under
 * the chart states it in one sentence, and the same derivation feeds the
 * Markdown export (`stageStandingsVerdict` in @aurora/shared).
 *
 * FORM (dataviz): emphasis — the client in the accent hue, everyone else in
 * context grey. A named competitor is tagged in text, never given a hue: the
 * ladder is about position, not identity.
 */
import type {
  StageStanding,
  StageStandings as StageStandingsData,
} from '@aurora/shared';
import { stageStandingsVerdict } from '@aurora/shared';
import { CONTEXT, TARGET } from './palette';

type LadderLine = {
  rank: number | null;
  company: string;
  count: number;
  rate: number;
  isTarget: boolean;
  tracked: boolean;
};

type Flag = 'path' | 'strongest' | 'weakest';

function Line({ line, cells }: { line: LadderLine; cells: number }): JSX.Element {
  const width = Math.max(line.count > 0 ? 2 : 0, Math.min(100, line.rate * 100));
  return (
    <div
      className={line.isTarget ? 'standing-row is-target' : 'standing-row'}
      title={`${line.company}: named in ${line.count} of ${cells} answers at this stage${line.rank !== null ? ` (#${line.rank})` : ''}`}
    >
      <span className="standing-rank">
        {line.rank !== null ? `#${line.rank}` : '—'}
      </span>
      <span className="standing-name">
        {line.company}
        {line.isTarget ? ' (you)' : ''}
        {line.tracked ? <span className="standing-tag">named</span> : null}
      </span>
      <span className="standing-track">
        <span
          className="standing-bar"
          style={{
            width: `${width}%`,
            background: line.isTarget ? TARGET : CONTEXT,
          }}
        />
      </span>
      <span className="standing-count">{line.count}</span>
    </div>
  );
}

function Panel({
  s,
  top,
  targetName,
  flags,
}: {
  s: StageStanding;
  top: number;
  targetName: string;
  flags: Flag[];
}): JSX.Element {
  const you: LadderLine = {
    rank: s.you.rank,
    company: targetName,
    count: s.you.count,
    rate: s.you.rate,
    isTarget: true,
    tracked: false,
  };
  // What sits between the ladder and the client's row, when they are apart.
  let divider: string | null = null;
  if (!s.you.inTop) {
    if (s.you.rank !== null) {
      const hidden = s.you.rank - top - 1;
      divider = hidden > 0 ? `⋯ ${hidden} more` : '⋯';
    } else if (s.you.count > 0) {
      divider = `⋯ outside the top ${s.listed}`;
    }
  }
  const isPath = flags.includes('path');
  const toGo = s.answersToCutoff ?? 0;
  return (
    <div className={isPath ? 'standings-panel is-path' : 'standings-panel'}>
      <div className="standing-head">
        <span className="standing-stage">{s.label}</span>
        <span className="muted">{s.scoredCells} answers</span>
      </div>
      {s.ladder.map((r) => (
        <Line
          key={r.company}
          cells={s.scoredCells}
          line={{
            rank: r.rank,
            company: r.company,
            count: r.count,
            rate: r.rate,
            isTarget: r.isTarget,
            tracked: r.tracked,
          }}
        />
      ))}
      {!s.you.inTop && (
        <>
          {divider && <div className="standing-gap">{divider}</div>}
          <Line line={you} cells={s.scoredCells} />
        </>
      )}
      <div className="standing-foot">
        {s.you.count === 0 && (
          <span className="standing-flag">Not surfaced at this stage</span>
        )}
        {isPath && (
          <span className="standing-flag is-path">
            Start here ·{' '}
            {toGo === 0
              ? `level with the top ${top}`
              : `+${toGo} ${toGo === 1 ? 'answer' : 'answers'} to reach the top ${top}`}
          </span>
        )}
        {flags.includes('strongest') && (
          <span className="standing-flag is-strong">Your strongest stage</span>
        )}
        {flags.includes('weakest') && (
          <span className="standing-flag is-weak">Your weakest stage</span>
        )}
      </div>
    </div>
  );
}

export function StageStandings({
  standings,
  targetName,
}: {
  standings: StageStandingsData;
  targetName: string;
}): JSX.Element | null {
  const { stages, top, strongest, weakest, shortestPath } = standings;
  if (stages.length === 0) return null;
  const verdict = stageStandingsVerdict(standings);
  const distinct = strongest && weakest && strongest.stage !== weakest.stage;

  return (
    <figure className="stage-standings">
      <p className="funnel-lede">
        The top {top} companies AI surfaced at each buyer-journey stage, ranked
        by the share of that stage&apos;s answers they appeared in — with{' '}
        <strong>{targetName}</strong> drawn on every ladder: in place when they
        make the top {top}, beneath it with their rank when they do not.
      </p>
      <div className="standings-grid">
        {stages.map((s) => {
          const flags: Flag[] = [];
          if (shortestPath?.stage === s.stage) flags.push('path');
          if (distinct && strongest?.stage === s.stage) flags.push('strongest');
          if (distinct && weakest?.stage === s.stage) flags.push('weakest');
          return (
            <Panel
              key={s.stage}
              s={s}
              top={top}
              targetName={targetName}
              flags={flags}
            />
          );
        })}
      </div>
      <div className="chart-legend">
        <span className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: TARGET }} />
          {targetName}
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: CONTEXT }} />
          Companies AI surfaced at that stage
        </span>
        <span className="chart-legend-item">
          <span className="standing-tag">named</span> a competitor you listed
        </span>
      </div>
      {verdict && (
        <figcaption className="chart-verdict">
          <strong>What it shows:</strong> {verdict.shows}
          {verdict.doNext && (
            <>
              {' '}
              <strong>What to do:</strong> {verdict.doNext}
            </>
          )}
        </figcaption>
      )}
    </figure>
  );
}
